import React, { useState, useEffect } from 'react';
import { 
  Avatar,
  Box,
  Button,
  Container,
  CssBaseline,
  Paper,
  TextField,
  Typography,
  Link,
  Divider,
  useTheme,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Swal from 'sweetalert2';
import userServiceInstance from "../../services/UserService";
import Logo from '../../assets/SRMD-Logo.png';

function Copyright(props) {
  return (
    <Typography variant="body2" color="secondary" align="center" {...props}>
      {'Copyright © '}
      <Link color="inherit" href="https://www.srmd.org/">
        SRMD
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

export default function Login({ darkMode, themeVariant = 'vibrant' }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Determine background based on theme
  const getBackgroundStyle = () => {
    if (themeVariant === 'vibrant') {
      return {
        background: theme.palette.login?.background || (
          darkMode 
            ? 'linear-gradient(135deg, #1a1a2e 0%, #262645 50%, #2c2155 100%)'
            : 'linear-gradient(135deg, #f5f7fb 0%, #e8effc 50%, #e0d9f6 100%)'
        )
      };
    } else if (themeVariant === 'sunset') {
      return {
        background: darkMode 
          ? 'linear-gradient(135deg, #2d1a1a 0%, #3e1c0a 50%, #4e2611 100%)'
          : 'linear-gradient(135deg, #fff8e1 0%, #ffecb3 50%, #ffe082 100%)',
      };
    } else if (themeVariant === 'ocean') {
      return {
        background: darkMode 
          ? 'linear-gradient(135deg, #0a1922 0%, #0c2e3e 50%, #11445e 100%)'
          : 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 50%, #90caf9 100%)',
      };
    } else if (themeVariant === 'minimalist') {
      return {
        background: darkMode 
          ? 'linear-gradient(135deg, #121420 0%, #1a1c2a 50%, #242736 100%)'
          : 'linear-gradient(135deg, #ffffff 0%, #f7f9fc 50%, #eef2f7 100%)',
      };
    } else if (themeVariant === 'corporate') {
      return {
        background: darkMode 
          ? 'linear-gradient(135deg, #0f1214 0%, #171c21 50%, #1e2429 100%)'
          : 'linear-gradient(135deg, #ffffff 0%, #f5f7fa 50%, #e4e7eb 100%)',
      };
    } else if (themeVariant === 'materialYou') {
      return {
        background: darkMode 
          ? 'linear-gradient(135deg, #121414 0%, #191c1c 50%, #1f2424 100%)'
          : 'linear-gradient(135deg, #ffffff 0%, #f5fafa 50%, #e0f2f1 100%)',
      };
    } else {
      return {
        background: darkMode 
          ? 'linear-gradient(135deg, #0d0d0d 0%, #121212 50%, #1e1e1e 100%)'
          : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 50%, #eeeeee 100%)',
      };
    }
  };
  
  // Get card style based on theme
  const getCardStyle = () => {
    if (themeVariant === 'vibrant') {
      return {
        borderRadius: 0,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        background: theme.palette.login?.cardBg || (
          darkMode 
            ? 'rgba(30, 30, 45, 0.7)'
            : 'rgba(255, 255, 255, 0.85)'
        ),
        backdropFilter: 'blur(8px)',
        border: `1px solid ${theme.palette.login?.cardBorder || (
          darkMode 
            ? 'rgba(139, 108, 181, 0.15)'
            : 'rgba(106, 133, 182, 0.1)'
        )}`,
      };
    } else if (themeVariant === 'sunset') {
      return {
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        background: darkMode 
          ? 'rgba(62, 28, 10, 0.7)' 
          : 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(8px)',
        border: darkMode 
          ? '1px solid rgba(255, 255, 255, 0.08)' 
          : '1px solid rgba(0, 0, 0, 0.04)',
      };
    } else if (themeVariant === 'ocean') {
      return {
        borderRadius: 16,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        background: theme.palette.background.paper,
        border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'}`,
      };
    } else if (themeVariant === 'materialYou') {
      return {
        borderRadius: 24,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        background: theme.palette.background.paper,
        border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'}`,
      };
    } else {
      return {
        borderRadius: theme.shape.borderRadius * 2,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
        background: theme.palette.background.paper,
        border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'}`,
      };
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    
    try {
      const response = await userServiceInstance.login(email, password);
      
      if (response.data.authorization === undefined) {
        Swal.fire({
          title: 'Error!',
          text: 'Invalid Email Or Password!',
          icon: 'error',
          confirmButtonText: 'Close',
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
        });
        setLoading(false);
        return;
      }
      
      localStorage.setItem('authToken', JSON.stringify(response.data.authorization));

      // Process language access
      let languageAccess = {};
      if (response.data.languageAccess) {
        if (typeof response.data.languageAccess === 'string') {
          try {
            languageAccess = JSON.parse(response.data.languageAccess);
          } catch (error) {
            console.error('Error parsing language access:', error);
          }
        } else {
          languageAccess = response.data.languageAccess;
        }
      }
      
      // Save user data
      const userData = {
        email: response.data.email || '',
        firstName: response.data.firstName || '',
        lastName: response.data.lastName || '',
        userType: response.data.userType || 'user',
        access: response.data.accessToken,
        languageAccess: languageAccess || {}
      };
      
      localStorage.setItem('authUser', JSON.stringify(userData));
      window.location.href = '/';
      
    } catch (error) {
      console.error('Login error:', error);
      Swal.fire({
        title: 'Error',
        text: 'An error occurred during login. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      });
      setLoading(false);
    }
  };

  // Update your return statement for better logo positioning and larger card
  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      ...getBackgroundStyle(),
      position: 'relative',
      overflow: 'hidden',
      transition: 'background 0.3s ease-in-out',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 50%)',
        opacity: darkMode ? 0.05 : 0.1,
        zIndex: 0
      }
    }}>
      <CssBaseline />
      
      {/* Add a centered logo at the top of the page */}
      <Box 
        sx={{ 
          width: '100%', 
          display: 'flex', 
          justifyContent: 'center',
          mt: 4,
          mb: -4, // Negative margin to pull the card closer
          position: 'relative',
          zIndex: 2
        }}
      >
        <Avatar 
          src={Logo} 
          alt="SRMD Logo" 
          sx={{ 
            width: 100, 
            height: 100, 
            backgroundColor: '#FFFFFF',
            boxShadow: darkMode ? '0 4px 16px rgba(0, 0, 0, 0.3)' : '0 4px 16px rgba(0, 0, 0, 0.15)',
            border: '4px solid',
            borderColor: darkMode ? 'rgba(139, 108, 181, 0.3)' : 'rgba(255, 255, 255, 0.9)',
            p: 1.5
          }}
        />
      </Box>
      
      <Container component="main" maxWidth="sm" sx={{ // Increased from xs to sm for a wider card
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center',
        flex: 1,
        position: 'relative',
        zIndex: 1,
        px: { xs: 2, sm: 4 } // Better padding on mobile
      }}>
        <Paper 
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 }, // Increased padding
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mt: 5, // Add margin top to make space for the logo
            ...getCardStyle()
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            mb: 3,
            width: '100%'
          }}>
            <Typography 
              component="h1" 
              variant="h3" // Larger heading
              sx={{ 
                fontWeight: 600,
                color: darkMode ? 'primary.light' : 'primary.main',
                letterSpacing: '0.5px',
                mt: 2 // Add space after the logo
              }}
            >
              Vaani
            </Typography>
            <Typography 
              variant="subtitle1"
              sx={{ 
                color: 'text.secondary',
                fontStyle: 'italic',
                textAlign: 'center',
                mt: 1,
                opacity: 0.85,
                fontSize: '1rem'
              }}
            >
              Realise One's True Self and Serve Others Selflessly
            </Typography>
          </Box>

          <Divider sx={{ 
            width: '100%', 
            mb: 3,
            '&::before, &::after': {
              borderColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
            }
          }}>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ px: 1, opacity: 0.7 }}
            >
              Sign In
            </Typography>
          </Divider>
          
          <Box component="form" onSubmit={handleSubmit} noValidate width="100%">
            <TextField
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              variant="outlined"
              InputProps={{
                sx: { 
                  borderRadius: themeVariant === 'vibrant' 
                    ? 12 
                    : (themeVariant === 'ocean' ? 25 : 
                       themeVariant === 'materialYou' ? 12 : 
                       theme.shape.borderRadius),
                  '& fieldset': {
                    borderColor: themeVariant === 'vibrant'
                      ? theme.palette.login?.inputBorder || (darkMode ? 'rgba(139, 108, 181, 0.3)' : 'rgba(106, 133, 182, 0.3)')
                      : (darkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'),
                  },
                  '&:hover fieldset': {
                    borderColor: themeVariant === 'vibrant'
                      ? theme.palette.login?.inputBorder || (darkMode ? 'rgba(139, 108, 181, 0.5)' : 'rgba(106, 133, 182, 0.5)')
                      : (darkMode ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)'),
                  },
                  transition: 'all 0.2s ease-in-out'
                }
              }}
            />
            <TextField
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              variant="outlined"
              InputProps={{
                sx: { 
                  borderRadius: themeVariant === 'vibrant' 
                    ? 12 
                    : (themeVariant === 'ocean' ? 25 : 
                       themeVariant === 'materialYou' ? 12 : 
                       theme.shape.borderRadius),
                  '& fieldset': {
                    borderColor: themeVariant === 'vibrant'
                      ? theme.palette.login?.inputBorder || (darkMode ? 'rgba(139, 108, 181, 0.3)' : 'rgba(106, 133, 182, 0.3)')
                      : (darkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'),
                  },
                  '&:hover fieldset': {
                    borderColor: themeVariant === 'vibrant'
                      ? theme.palette.login?.inputBorder || (darkMode ? 'rgba(139, 108, 181, 0.5)' : 'rgba(106, 133, 182, 0.5)')
                      : (darkMode ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)'),
                  },
                  transition: 'all 0.2s ease-in-out'
                }
              }}
            />

            <Button
              disabled={email === '' || password === '' || loading}
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2,
                py: 1.5,
                position: 'relative',
                borderRadius: themeVariant === 'vibrant' 
                  ? 12
                  : (themeVariant === 'ocean' ? 25 : 
                     themeVariant === 'materialYou' ? 12 : 
                     theme.shape.borderRadius),
                background: themeVariant === 'vibrant'
                  ? theme.palette.login?.buttonGradient || 'linear-gradient(135deg, #6a85b6 0%, #8b6cb5 100%)'
                  : theme.palette.primary.main,
                '&:hover': {
                  background: themeVariant === 'vibrant'
                    ? (darkMode ? 'linear-gradient(135deg, #7a59a8 0%, #a989d5 100%)' : 'linear-gradient(135deg, #5c78af 0%, #7a59a8 100%)')
                    : theme.palette.primary.dark,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              {loading ? (
                <CircularProgress 
                  size={24}
                  sx={{
                    color: '#fff',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-12px',
                    marginLeft: '-12px',
                  }}
                />
              ) : 'Sign In'}
            </Button>
          </Box>
          
          
        </Paper>
        
        <Copyright sx={{ mt: 4, mb: 4 }} />
      </Container>
    </Box>
  );
}
